package info.rezident.app.ui.components.widgets

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import info.rezident.app.core.AppTracking
import info.rezident.app.data.user.UserRepository
import info.rezident.app.ui.theme.AppTheme
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px

/**
 * Compact paywall strip component for mobile recommendations
 * Shows a clickable strip with lock icon, text, price, and chevron
 *
 * @param priceText The price to display (e.g., "24.99 RON")
 * @param onClick Callback when the strip is clicked
 * @param modifier Additional modifiers to apply
 */
@Composable
fun CompactPaywallStrip(
    priceText: String?,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    id: String? = null
) {
    val userProfile = UserRepository.user.value
    val academicYear = userProfile?.academicYear ?: "unknown"
    val priceVariant = userProfile?.priceVariant?.name ?: "unknown"
    Box(
        modifier = modifier
            .fillMaxWidth()
            .backgroundColor(AppTheme.colors.surface)
            .border(1.px, LineStyle.Solid, AppTheme.colors.accent)
            .borderRadius(AppTheme.shapes.mediumRound)
            .padding(16.px)
            .cursor(Cursor.Pointer)
            .onClick { event ->
                event.stopPropagation()
                AppTracking.logEvent(
                    AppTracking.EVENT_RECOMMENDATIONS_PAYWALL_COMPACT_CLICK,
                    mapOf(
                        AppTracking.PARAM_TEMPLATE_ID to 1,
                        AppTracking.PARAM_ACADEMIC_YEAR to academicYear,
                        AppTracking.PARAM_PRICE_VARIANT to priceVariant
                    )
                )
                onClick()
            }
            .then(if (id != null) Modifier.id(id) else Modifier)
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.Top
        ) {
            // Left side: All content in a column
            Column(
                modifier = Modifier.weight(1f).fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(8.px)
            ) {
                // Title row with lock emoji inline
                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.px),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    SpanText(
                        text = "🔒",
                        modifier = Modifier.fontSize(20.px)
                    )
                    SpanText(
                        text = "Analiză Detaliată",
                        modifier = AppTheme.typography.body1Medium.toModifier()
                            .color(AppTheme.colors.text)
                    )
                }

                // Description
                SpanText(
                    text = "Deblochează toate explicațiile și recomandările",
                    modifier = AppTheme.typography.body2.toModifier()
                        .color(AppTheme.colors.textSecondary)
                        .lineHeight(1.3)
                )

                // Price badge
                Box(
                    modifier = Modifier
                        .backgroundColor(AppTheme.colors.accent)
                        .borderRadius(4.px)
                        .padding(leftRight = 8.px, topBottom = 4.px)
                        .align(Alignment.Start),
                    contentAlignment = Alignment.Center
                ) {
                    SpanText(
                        text = priceText ?: "24.99 RON",
                        modifier = AppTheme.typography.body2.toModifier()
                            .color(AppTheme.colors.onAccent)
                            .fontWeight(FontWeight.SemiBold)
                    )
                }
            }
        }

        // Right side: Chevron aligned to the right
        SpanText(
            text = "›",
            modifier = Modifier
                .fontSize(24.px)
                .color(AppTheme.colors.textTertiary)
                .flexShrink(0)
                .margin(left = 12.px)
                .align(Alignment.CenterEnd)
        )
    }
}
